import axios from "axios"
import { Parking } from "../../__generated__/proto/itech/motorist/pksha/v1/parking"
import {
  GetParkingRequest,
  GetParkingResponse,
  ListParkingsRequest,
  ListParkingsResponse,
  ListTransactionsResponse,
  ReserveResponse,
  CalculateCancellationFeeResponse,
  CancelReservationResponse,
  AcceptEntryResponse,
  RejectEntryResponse,
} from "../../__generated__/proto/itech/motorist/pksha/v1/pksha_api"
import * as config from "../../config"
import {
  ExtendsListTransactionsRequest,
  ExtendsReserveRequest,
  ExtendsCancelReservationRequest,
  ExtendsCalculateCancellationFeeRequest,
  ExtendsAcceptEntryRequest,
  ExtendsRejectEntryRequest,
} from "../type-extensions"
import { Failure, Result, Success } from "./result"

// MARK: - Interfaces
export interface ParkingServiceType {
  getParking(
    request: GetParkingRequest
  ): Promise<Result<Parking, GetParkingError>>
  listParkings(
    request: ListParkingsRequest
  ): Promise<Result<ListParkingsResponse, ListParkingsError>>
  listTransactions(
    request: ExtendsListTransactionsRequest
  ): Promise<Result<ListTransactionsResponse, ListTransactionsError>>
  reserve(
    request: ExtendsReserveRequest
  ): Promise<Result<ReserveResponse, ReserveError>>
  calculateCancellationFee(
    request: ExtendsCalculateCancellationFeeRequest
  ): Promise<
    Result<CalculateCancellationFeeResponse, CalculateCancellationFeeError>
  >
  cancelReservation(
    request: ExtendsCancelReservationRequest
  ): Promise<Result<CancelReservationResponse, CancelReservationError>>
  acceptEntry(
    request: ExtendsAcceptEntryRequest
  ): Promise<Result<AcceptEntryResponse, AcceptEntryError>>
  rejectEntry(
    request: ExtendsRejectEntryRequest
  ): Promise<Result<RejectEntryResponse, RejectEntryError>>
}

// MARK: - Implements
export class ParkingService implements ParkingServiceType {
  private async sendRequest<T>(
    method: "get" | "post",
    url: string,
    data: Record<string, unknown>,
    headers: Record<string, string>
  ): Promise<Result<T, Error>> {
    try {
      const response =
        method === "get"
          ? await axios.get<T>(url, { headers, params: data })
          : await axios.post<T>(url, data, { headers })

      return new Success(response.data)
    } catch (error) {
      console.error(error)
      return new Failure(error as Error)
    }
  }
  async getParking(
    request: GetParkingRequest
  ): Promise<Result<Parking, GetParkingError>> {
    try {
      const response = await axios.post<GetParkingResponse>(
        `${config.PKSHA_HTTP_API_URL}/parking`,
        {
          headers: { "Content-Type": "application/json" },
          name: request.name,
        }
      )

      if (response.data.parking) {
        return new Success(response.data.parking)
      } else {
        return new Failure(new GetParkingError("Parking not found"))
      }
    } catch (error) {
      console.error(error)
      return new Failure(new GetParkingError())
    }
  }

  async listParkings(
    request: ListParkingsRequest
  ): Promise<Result<ListParkingsResponse, ListParkingsError>> {
    return this.sendRequest<ListParkingsResponse>(
      "post",
      `${config.PKSHA_HTTP_API_URL}/parkings`,
      {
        page_size: request.pageSize,
        page_token: request.pageToken,
        bounding_box: request.boundingBox,
      },
      { "Content-Type": "application/json" }
    )
  }

  async listTransactions(
    request: ExtendsListTransactionsRequest
  ): Promise<Result<ListTransactionsResponse, ListTransactionsError>> {
    return this.sendRequest<ListTransactionsResponse>(
      "get",
      `${config.PKSHA_HTTP_API_URL}/transactions`,
      { page_size: request.pageSize },
      {
        "Content-Type": "application/json",
        Authorization: `Basic ${request.token}`,
      }
    )
  }

  async reserve(
    request: ExtendsReserveRequest
  ): Promise<Result<ReserveResponse, ReserveError>> {
    if (!request.token) {
      return new Failure(new ReserveError("Authorization token is missing"))
    }

    return this.sendRequest<ReserveResponse>(
      "post",
      `${config.PKSHA_HTTP_API_URL}/reserve`,
      {
        parking_space_name: request.parkingSpaceName,
        request_id: request.requestId,
        license_number: request.licenseNumber,
        required_handicapped_space: request.requireHandicappedSpace,
      },
      {
        "Content-Type": "application/json",
        Authorization: `Basic ${request.token}`,
      }
    )
  }

  async calculateCancellationFee(
    request: ExtendsCalculateCancellationFeeRequest
  ): Promise<
    Result<CalculateCancellationFeeResponse, CalculateCancellationFeeError>
  > {
    return this.sendRequest<CalculateCancellationFeeResponse>(
      "post",
      `${config.PKSHA_HTTP_API_URL}/${request.transactionName}:calculate-cancellation-fee`,
      {},
      {
        "Content-Type": "application/json",
        Authorization: `Basic ${request.token}`,
      }
    )
  }

  async cancelReservation(
    request: ExtendsCancelReservationRequest
  ): Promise<Result<CancelReservationResponse, CancelReservationError>> {
    return this.sendRequest<CancelReservationResponse>(
      "post",
      `${config.PKSHA_HTTP_API_URL}/${request.transactionName}:cancel-reservation`,
      {},
      {
        "Content-Type": "application/json",
        Authorization: `Basic ${request.token}`,
      }
    )
  }

  async acceptEntry(
    request: ExtendsAcceptEntryRequest
  ): Promise<Result<AcceptEntryResponse, AcceptEntryError>> {
    return this.sendRequest<AcceptEntryResponse>(
      "post",
      `${config.PKSHA_HTTP_API_URL}/${request.transactionName}:accept-entry`,
      {},
      {
        "Content-Type": "application/json",
        Authorization: `Basic ${request.token}`,
      }
    )
  }

  async rejectEntry(
    request: ExtendsRejectEntryRequest
  ): Promise<Result<RejectEntryResponse, RejectEntryError>> {
    return this.sendRequest<RejectEntryResponse>(
      "post",
      `${config.PKSHA_HTTP_API_URL}/${request.transactionName}:reject-entry`,
      {},
      {
        "Content-Type": "application/json",
        Authorization: `Basic ${request.token}`,
      }
    )
  }
}

// MARK: - Errors

export class GetParkingError extends Error {}
export class ListParkingsError extends Error {}
export class ReserveError extends Error {}
export class ListTransactionsError extends Error {}
export class CalculateCancellationFeeError extends Error {}
export class CancelReservationError extends Error {}
export class AcceptEntryError extends Error {}
export class RejectEntryError extends Error {}
